import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, zip } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  ChannelType,
  DatasetSelectedRecipientFilter,
  DistributionListCount,
  DocumentsRefRelatedDocumentsRequest,
  MassNotificationSelectedRecipient,
  MassNotificationTemplate,
  MassRecipientType,
  ToRecipientSettings
} from 'src/app/modules/communications/store/communications/communications.model';
import { RecipientListSelectComponent, SelectedRecipientInfo } from '../recipient-list-select/recipient-list-select.component';
import { FieldDataType, TableFilter } from 'src/app/modules/core/store/models';
import { cloneDeep } from 'lodash-es';
import { RecipientSelectionService } from 'src/app/modules/communications/services/recipient-selection-service';
import { TemplateDesignerService } from '@app/modules/communications/services/template-designer.service';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
import { take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { GUID_EMPTY } from '@app/core/constants';

@Component({
  selector: 'topo-comms-recipients-selection',
  templateUrl: './recipients-selection.component.html',
  styleUrls: ['./recipients-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RecipientsSelectionComponent
    }
  ],
  standalone: false
})
export class RecipientsSelectionComponent implements OnInit, OnDestroy {
  @Input() set toRecipientSettings(value: ToRecipientSettings) {
    this.selectedRecipients = cloneDeep(value?.recipients);
    this.datasetsSelected = cloneDeep(value?.datasetRecipients?.filter(d => d.documentCount > 0));

    this.datasetsSelected?.forEach(d => {
      this.recipientSelectionService.getTableFilterDisplay(d, null).subscribe(value => {
        d.filterDisplay = value;
      });
      const info = this.getRelatedAssetsInfo(d);
      d.relatedAssetName = info?.name;
      d.relatedAssetIcon = info?.icon;
      d.relatedAssetIsCustomIcon = info?.isCustom;
      d.relatedAssetColor = info?.color;
      this.loadDatasetRecipientRelatedCounts(d, false);
    });

    this.getDistListMembersCount();
    this.calculateTotalRecipients();
    this.updateSelectedMembers();
  }

  @Input() template: MassNotificationTemplate;

  @Input() includeUsers: boolean;
  @Input() includeGroups: boolean;
  @Input() includeDistro: boolean;
  @Input() includeDatasets: boolean;

  _addToTotalRecipients: number;
  @Input() set addToTotalRecipients(value: number) {
    this._addToTotalRecipients = value;
    this.getDistListMembersCount();
    this.calculateTotalRecipients();
  }
  @Input() showAddedRecipients: boolean;
  @Input() showTemplateRecipients: boolean;

  @Output() selectedRecipientsChanged = new EventEmitter<ToRecipientSettings>();

  @ViewChild('listSelect') listSelection: RecipientListSelectComponent;

  destroy$: Subject<void> = new Subject<void>();
  channelTypes: ChannelType[];
  selectedRecipients?: MassNotificationSelectedRecipient[];
  selectedMembers: any[];
  recipientsSelectionActive: boolean;
  selectedRecipientTypeUId: string;
  datasetFilter: TableFilter;
  recipientFilter: DatasetSelectedRecipientFilter;
  datasetsSelected: DatasetSelectedRecipientFilterEx[];

  showConfirmation: boolean;
  confirmationTitle: string;
  confirmationMessage: string;
  confirmationContext: { confirmed: boolean; name: string; data: any };
  totalRecipients: number;
  addedRecipients: number;
  distMemberContacts: { [uid: string]: number };

  distRecipients: string[];
  userRecipients: string[];
  groupRecipients: string[];

  constructor(
    private translateService: TranslateService,
    private recipientSelectionService: RecipientSelectionService,
    private templateDesignerService: TemplateDesignerService,
    private commApiService: CommunicationsApiService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.recipientSelectionService.init();
    this.templateDesignerService.initDatasets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  inlineRecipientsSelected(members: any[]): void {
    if (!!!members) {
      return;
    }
    if (!!!this.selectedRecipients) {
      this.selectedRecipients = [];
    }
    let needsRefresh = false;
    // remove all custom
    this.selectedRecipients
      .filter(r => r.type === MassRecipientType.Custom)
      .forEach(r => {
        const index = this.selectedRecipients.findIndex(cr => cr.displayName === r.displayName && cr.type === MassRecipientType.Custom);
        if (index >= 0) {
          needsRefresh = true;
          this.selectedRecipients.splice(index, 1);
        }
      });
    // add all custom
    members
      .filter(m => m.memberType === undefined || m.memberType === MassRecipientType.Custom)
      .forEach(member => {
        if (!this.selectedRecipients?.find(r => r.displayName == member.displayName)) {
          needsRefresh = true;
          this.selectedRecipients.push({
            uId: uuidv4(),
            type: MassRecipientType.Custom,
            displayName: member.displayName
          });
        }
      });

    members
      .filter(m => m.memberType !== MassRecipientType.Custom)
      .forEach(member => {
        needsRefresh = false;
        this.addRecipientData(
          { type: member.memberType, selectInfo: { selectAll: false, documentUIds: [member.uId] }, displayName: member.displayName } as SelectedRecipientInfo,
          false
        );
      });

    if (needsRefresh) {
      this.sendRecipientsChanged();
    }
    this.updateSelectedMembers();
  }

  private updateSelectedMembers(): void {
    this.selectedMembers = this.selectedRecipients
      ?.filter(r => r.type === MassRecipientType.Custom)
      .map(r => {
        return {
          memberType: r.type,
          uId: r.uId,
          displayName: r.displayName
        };
      });
  }

  private getRelatedAssetsInfo(datasetInfo: DatasetSelectedRecipientFilterEx): {
    name: string;
    field: string;
    icon: string;
    isCustom: boolean;
    color: string;
  } {
    const dataset = this.templateDesignerService.datasets?.find(d => d.uId === datasetInfo?.datasetUId);
    const field = dataset?.schema?.fields.find(f => f.dataType === FieldDataType.ReferenceCriteria);
    if (!!field?.settings?.targetDatasetUId) {
      const targetDataset = this.templateDesignerService.datasets?.find(d => d.uId === field?.settings?.targetDatasetUId);
      return {
        name: targetDataset?.name,
        field: field.systemName!,
        icon: targetDataset?.iconPath,
        isCustom: targetDataset?.isCustomIcon,
        color: targetDataset?.defaultColor
      };
    } else {
      return null;
    }
  }

  addRecipient(): void {
    this.recipientFilter = null;
    this.datasetFilter = null;
    this.selectedRecipientTypeUId = null;
    this.recipientsSelectionActive = true;
  }

  private calculateTotalRecipients() {
    this.distRecipients = [];
    this.selectedRecipients
      ?.filter(r => r.type === MassRecipientType.DistributionList)
      ?.forEach(r => {
        this.distRecipients.push(r.uId);
      });
    this.userRecipients = [];
    this.selectedRecipients
      ?.filter(r => r.type === MassRecipientType.User)
      ?.forEach(r => {
        this.userRecipients.push(r.uId);
      });

    this.groupRecipients = [];
    this.selectedRecipients
      ?.filter(r => r.type === MassRecipientType.Group)
      ?.forEach(r => {
        this.groupRecipients.push(r.uId);
      });

    this.addedRecipients = this.selectedRecipients?.filter(r => r.type !== MassRecipientType.DistributionList).length ?? 0;
    this.datasetsSelected?.forEach(r => {
      let recipientCount = r?.documentCount;
      let count = recipientCount * this.templateDesignerService.getNumberOfContactFields(r.datasetUId);
      if (r.sendToRelatedAssets == true) {
        count += r.relatedCount * this.templateDesignerService.getNumberOfContactFields(r.relatedDatasetUId);
        recipientCount += r.relatedCount;
      }
      r.contactCount = count;
      r.contactCountDisplay = r.limitedCounts === true ? `> ${r.contactCount}` : r.contactCount.toString();
      this.addedRecipients += recipientCount;
    });

    this.addedRecipients += this.totalDistListContacts;

    this.totalRecipients = this.addedRecipients;
    if (!!this._addToTotalRecipients) {
      this.totalRecipients += this._addToTotalRecipients;
    }
    this.changeDetector.markForCheck();
  }

  private getDistListMembersCount(): void {
    this.distMemberContacts = {};

    this.recipientSelectionService.getDistListMembersCount(this.selectedRecipients).subscribe(resp => {
      // * NOTE * bandaid solution to handling stringified json references that aren't deleted
      // API returns empty guid for the datasetUId when the dist list is not found
      const notFoundUIds = new Set<string>();

      resp.forEach(r => {
        if (r.datasetUId === GUID_EMPTY) {
          notFoundUIds.add(r.distributionListUId);
        } else {
          this.distMemberContacts[r.distributionListUId] = r.count * this.templateDesignerService.getNumberOfContactFields(r.datasetUId);
        }
      });

      this.distRecipients = this.distRecipients?.filter(uId => !notFoundUIds.has(uId)) ?? [];
      this.selectedRecipients = this.selectedRecipients?.filter(r => !notFoundUIds.has(r.uId)) ?? [];

      this.changeDetector.markForCheck();
      this.calculateTotalRecipients();
    });
  }

  get totalDistListContacts(): number {
    let total = 0;
    if (!!this.distMemberContacts) {
      Object.values(this.distMemberContacts).forEach(v => {
        total += v;
      });
    }
    return total;
  }

  private addRecipientData(data: SelectedRecipientInfo, mergeMode: boolean) {
    if (!!!this.selectedRecipients) {
      this.selectedRecipients = [];
    }
    if (data.type === MassRecipientType.DistributionList) {
      if (mergeMode) {
        this.distRecipients?.forEach(r => {
          const index = this.selectedRecipients.findIndex(s => s.uId == r);
          this.selectedRecipients.splice(index, 1);
        });
      }

      data.selectInfo.documentUIds.forEach(d => {
        if (!this.selectedRecipients?.find(r => r.uId == d)) {
          this.selectedRecipients.push({
            uId: d,
            type: data.type,
            displayName: data.displayName
          });
        }
      });
    } else if (data.type === MassRecipientType.User) {
      if (mergeMode) {
        this.userRecipients?.forEach(r => {
          const index = this.selectedRecipients.findIndex(s => s.uId == r);
          this.selectedRecipients.splice(index, 1);
        });
      }
      data.selectInfo.documentUIds.forEach(d => {
        if (!this.selectedRecipients?.find(r => r.uId == d)) {
          this.selectedRecipients.push({
            uId: d,
            type: data.type,
            displayName: data.displayName
          });
        }
      });
    } else if (data.type === MassRecipientType.Group) {
      if (mergeMode) {
        this.groupRecipients?.forEach(r => {
          const index = this.selectedRecipients.findIndex(s => s.uId == r);
          this.selectedRecipients.splice(index, 1);
        });
      }
      data.selectInfo.documentUIds.forEach(d => {
        if (!this.selectedRecipients?.find(r => r.uId == d)) {
          this.selectedRecipients.push({
            uId: d,
            type: data.type,
            displayName: data.displayName
          });
        }
      });
    } else if (data.type === MassRecipientType.Document) {
      if (!!!this.datasetsSelected) {
        this.datasetsSelected = [];
      }

      this.confirmationContext = null;

      let datasetInfo = this.datasetsSelected.find(d => d.datasetUId == data.parentUId);
      if (!datasetInfo) {
        datasetInfo = {
          uId: data.uId,
          datasetUId: data.parentUId,
          datasetName: data.parentName,
          datasetIcon: data.parentIcon,
          documentCount: 0,
          filter: {} as TableFilter,
          selectedUIds: [],
          sendToRelatedAssets: false
        };

        this.datasetsSelected.push(datasetInfo);
      }

      datasetInfo.datasetUId = data.parentUId;
      datasetInfo.datasetName = data.parentName;
      datasetInfo.datasetIcon = data.parentIcon;
      datasetInfo.documentCount = data.documentCount;
      datasetInfo.filter = data.filter;

      if (mergeMode) {
        datasetInfo.selectedUIds = [];
      }
      data.selectInfo.documentUIds.forEach(d => {
        if (!datasetInfo.selectedUIds?.find(r => r == d)) {
          datasetInfo.selectedUIds.push(d);
        }
      });
      const info = this.getRelatedAssetsInfo(datasetInfo);
      datasetInfo.relatedAssetName = info?.name;
      datasetInfo.relatedAssetIcon = info?.icon;
      datasetInfo.relatedAssetIsCustomIcon = info?.isCustom;
      datasetInfo.relatedAssetColor = info?.color;

      this.loadDatasetRecipientRelatedCounts(datasetInfo, false);
    }

    this.sendRecipientsChanged();
  }

  private sendRecipientsChanged(): void {
    this.getDistListMembersCount();
    this.calculateTotalRecipients();
    this.notifyRecipientsChanged();
  }

  private notifyRecipientsChanged(): void {
    const settings = {
      recipients: this.selectedRecipients,
      datasetRecipients: this.datasetsSelected
    } as ToRecipientSettings;

    this.selectedRecipients = cloneDeep(settings.recipients);
    this.selectedRecipientsChanged.emit(settings);
  }

  recipientsSelected(data: SelectedRecipientInfo): void {
    this.addRecipientData(data, true);
  }

  recipientsClosed(): void {
    this.recipientsSelectionActive = false;
  }

  getSendToRelatedAssets(datasetInfo: DatasetSelectedRecipientFilter): boolean {
    return datasetInfo?.sendToRelatedAssets;
  }

  onSendToRelatedAssetsChanged(datasetInfo: DatasetSelectedRecipientFilter, event: any): void {
    event?.stopPropagation();

    const hasSendToRelated = this.getSendToRelatedAssets(datasetInfo);
    datasetInfo.sendToRelatedAssets = !hasSendToRelated;
    this.calculateTotalRecipients();
    this.changeDetector.detectChanges();
    this.sendRecipientsChanged();
  }

  private loadDatasetRecipientRelatedCounts(datasetInfo: DatasetSelectedRecipientFilter, sendUpdate: boolean): void {
    const infoToUpdate = this.datasetsSelected.find(a => a.datasetUId == datasetInfo.datasetUId);

    if (!!!infoToUpdate) {
      return;
    }

    this.recipientSelectionService.getDatasetDocumentsRelated(infoToUpdate, true).subscribe(res => {
      const totalCount = res.totalCount;
      infoToUpdate.relatedCount = totalCount;
      infoToUpdate.relatedContactCount = totalCount * this.templateDesignerService.getNumberOfContactFields(res.targetDatasetUId);
      infoToUpdate.relatedCountDisplay = res.limitedCounts === true ? `> ${totalCount}` : totalCount.toString();
      infoToUpdate.relatedDatasetUId = res.targetDatasetUId;
      this.calculateTotalRecipients();
      this.changeDetector.markForCheck();
      if (sendUpdate) {
        this.sendRecipientsChanged();
      }
    });
  }

  getSelectedUIds = (type: MassRecipientType, parentUId: string): string[] => {
    if (type === MassRecipientType.Document) {
      const selectedDataset = this.datasetsSelected?.find(d => d.datasetUId === parentUId);
      return selectedDataset?.selectedUIds;
    } else {
      return this.selectedRecipients?.filter(d => d.type === type)?.map(d => d.uId);
    }
  };

  getSelectedFilter = (filterUId: string): TableFilter => {
    return this.datasetsSelected?.find(d => d.uId === filterUId)?.filter;
  };

  isDuplicateFilter = (uId: string, datasetUId: string, filter: TableFilter) => {
    if (!!this.datasetsSelected) {
      for (const datasetInfo of this.datasetsSelected) {
        if (uId !== datasetInfo.uId && datasetInfo.datasetUId === datasetUId && this.isSameTableFilter(datasetInfo?.filter, filter)) {
          return true;
        }
      }
    }
    return false;
  };

  onManageDist(): void {
    this.selectedRecipientTypeUId = this.listSelection?.distRecipientTypeUId;
    this.recipientsSelectionActive = true;
  }
  onManageUsers(): void {
    this.selectedRecipientTypeUId = this.listSelection?.userRecipientTypeUId;
    this.recipientsSelectionActive = true;
  }
  onManageGroups(): void {
    this.selectedRecipientTypeUId = this.listSelection?.groupRecipientTypeUId;
    this.recipientsSelectionActive = true;
  }
  onManageDataset(uId: string): void {
    this.recipientFilter = this.datasetsSelected?.find(d => d.uId == uId);
    this.selectedRecipientTypeUId = this.recipientFilter?.datasetUId;
    this.recipientsSelectionActive = true;
  }

  onDeleteDist(event: any): void {
    event?.stopPropagation();
    this.confirmationTitle = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-TITLE');
    this.confirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-MESSAGE');
    this.confirmationContext = { confirmed: false, name: 'deleteDist', data: null };
    this.showConfirmation = true;
  }

  onDeleteUsers(event: any): void {
    event?.stopPropagation();
    this.confirmationTitle = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-TITLE');
    this.confirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-MESSAGE');
    this.confirmationContext = { confirmed: false, name: 'deleteUsers', data: null };
    this.showConfirmation = true;
  }

  onDeleteGroups(event: any): void {
    event?.stopPropagation();
    this.confirmationTitle = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-TITLE');
    this.confirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-MESSAGE');
    this.confirmationContext = { confirmed: false, name: 'deleteGroups', data: null };
    this.showConfirmation = true;
  }

  onDeleteDataset(datasetUId: string, event: any): void {
    event?.stopPropagation();
    this.confirmationTitle = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-TITLE');
    this.confirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-DELETE-RECIPIENTS-MESSAGE');
    this.confirmationContext = { confirmed: false, name: 'deleteDataset', data: datasetUId };
    this.showConfirmation = true;
  }

  onConfirm(confirmed: boolean) {
    this.showConfirmation = false;
    if (confirmed) {
      if (this.confirmationContext?.name === 'addRecipient') {
        this.confirmationContext.confirmed = true;
        this.addRecipientData(this.confirmationContext.data, false);
      } else if (this.confirmationContext?.name === 'deleteUsers') {
        this.confirmationContext.confirmed = true;
        this.deleteUsers();
      } else if (this.confirmationContext?.name === 'deleteGroups') {
        this.confirmationContext.confirmed = true;
        this.deleteGroups();
      } else if (this.confirmationContext?.name === 'deleteDist') {
        this.confirmationContext.confirmed = true;
        this.deleteDist();
      } else if (this.confirmationContext?.name === 'deleteDataset') {
        this.confirmationContext.confirmed = true;
        this.deleteDataset(this.confirmationContext.data);
      }
    }
  }

  private deleteUsers(): void {
    this.userRecipients?.forEach(r => {
      const index = this.selectedRecipients.findIndex(s => s.uId == r);
      this.selectedRecipients.splice(index, 1);
    });
    this.sendRecipientsChanged();
  }

  private deleteGroups(): void {
    this.groupRecipients?.forEach(r => {
      const index = this.selectedRecipients.findIndex(s => s.uId == r);
      this.selectedRecipients.splice(index, 1);
    });
    this.sendRecipientsChanged();
  }

  private deleteDist(): void {
    this.distRecipients?.forEach(r => {
      const index = this.selectedRecipients.findIndex(s => s.uId == r);
      this.selectedRecipients.splice(index, 1);
    });
    this.sendRecipientsChanged();
  }

  private deleteDataset(uId: string): void {
    const datasetIndex = this.datasetsSelected?.findIndex(d => d.datasetUId == uId);
    if (datasetIndex >= 0) {
      this.datasetsSelected.splice(datasetIndex, 1);
    }
    this.sendRecipientsChanged();
  }

  private isSameTableFilter(src: TableFilter, dst: TableFilter): boolean {
    if ((src?.filters || []).length !== (dst?.filters || []).length) {
      return false;
    }

    if (!!!src?.filters && !!!dst?.filters) {
      return true;
    }

    for (const srcFilter of src?.filters || []) {
      const destFilter = (dst?.filters || []).find(f => f.systemName == srcFilter?.systemName);
      if (!!!destFilter) {
        return false;
      }
      for (const srcValue of srcFilter.values) {
        if (!destFilter.values?.some(v => v.uId === srcValue.uId)) {
          return false;
        }
      }
    }

    return true;
  }
}

export interface DatasetSelectedRecipientFilterEx extends DatasetSelectedRecipientFilter {
  filterDisplay?: string;
  relatedAssetName?: string;
  // icon info
  relatedAssetIcon?: string;
  relatedAssetIsCustomIcon?: boolean;
  relatedAssetColor?: string;
  //
  contactCount?: number;
  contactCountDisplay?: string;
}
